/**
 * Pantalla de busqueda general: una caja, tres niveles, resultados agrupados.
 *
 * El pedido del dueño, textual: «que busques el nombre de una administracion y
 * te salgan administradores y consorcios bajo esa administracion, y que pueda
 * buscar por separado cada cosa».
 *
 * Las tres decisiones de esta pantalla
 * ------------------------------------
 * 1. **El texto vive en la URL** (`/buscar?q=…&tipo=…`). Una busqueda se comparte,
 *    se vuelve atras con el navegador y se puede dejar abierta en una pestaña.
 *    Ademas es lo que permite que la paleta de comandos (Ctrl+K) mande aca lo que
 *    ya se habia escrito, sin que haya que tipearlo de nuevo.
 * 2. **Los rotulos los manda el servidor.** No hay una sola palabra del oficio
 *    escrita en este archivo: ni «administracion», ni «consorcio», ni el orden de
 *    los grupos. Vienen en la respuesta (ver `api.ts`). Es lo mismo que ya hace
 *    el widget «¿Que es este contacto?», y por el mismo motivo: el renderer es
 *    generico.
 * 3. **El camino se muestra siempre.** Un consorcio encontrado por el nombre de
 *    su administracion, sin el camino a la vista, se lee como un error del
 *    buscador: «yo busque Kloster y esto se llama Torre Rivadavia». Con el camino
 *    —y con el motivo, «por su administracion»— se lee como lo que es.
 *
 * Lo que esta pantalla NO hace
 * ----------------------------
 * No escribe nada. No hay un boton que cree, edite, emita ni facture: se busca y
 * se entra. Del lado del cliente eso esta hecho cumplir por el tipo — la unica
 * llamada que importa este archivo es de solo lectura.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import {
  ICONOS_DE_MENU,
  IconChevronRight,
  IconDot,
  IconSearch,
  IconSpinner,
} from '@/components/shell/icons';
import { recordPath } from '@/components/shell/menu-utils';
import { Badge, Card, EmptyState, ErrorState, PageHeader } from '@/components/shell/primitives';
import { ApiError } from '@/lib/api';
import { useSessionStore } from '@/store/session';
import {
  buscarEnLaCartera,
  esRespuestaValida,
  TIPO_TODO,
  type GrupoDeBusqueda,
  type RespuestaDeBusqueda,
} from '@/views/busqueda/api';

/**
 * Cuanto se espera despues de la ultima tecla antes de preguntarle al servidor.
 *
 * 250 ms: por debajo se dispara una consulta por letra sobre una tabla de 3.770
 * filas; por encima se siente lento. El valor no se escribe dos veces — el
 * mismo temporizador es el que sincroniza la URL, asi que el historial del
 * navegador tampoco se llena con una entrada por tecla.
 */
const ESPERA_MS = 250;

/** Como se lee cada motivo. El servidor manda el codigo; el texto es de la pantalla. */
const TEXTO_DEL_MOTIVO: Record<string, string> = {
  administracion: 'por su administración',
  administrador: 'por su administrador',
};

export function BusquedaScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');

  const consultaUrl = searchParams.get('q') ?? '';
  const tipoUrl = searchParams.get('tipo') ?? TIPO_TODO;

  // Dos estados y no uno: lo que se ve tipeado (`texto`) y lo que se consulta
  // (`consultaUrl`). Sin esa separacion, cada tecla dispararia una consulta y
  // una entrada de historial.
  const [texto, setTexto] = useState(consultaUrl);
  const cajaRef = useRef<HTMLInputElement>(null);

  // El foco al entrar: se llega aca para escribir. Que haya que hacer un click
  // mas para poder tipear es exactamente la friccion que una pantalla de
  // busqueda no puede tener.
  useEffect(() => {
    cajaRef.current?.focus();
  }, []);

  // Si la URL cambia por afuera (el boton «atras», o un enlace de la paleta con
  // otra consulta), la caja se pone al dia. La comparacion evita pisar lo que la
  // persona esta escribiendo en este mismo momento.
  useEffect(() => {
    setTexto((actual) => (actual === consultaUrl ? actual : consultaUrl));
  }, [consultaUrl]);

  // El texto tipeado baja a la URL despues de la espera. `replace: true` para que
  // escribir «Kloster» deje UNA entrada en el historial y no siete.
  useEffect(() => {
    if (texto === consultaUrl) return;
    const temporizador = window.setTimeout(() => {
      setSearchParams(
        (actual) => {
          const copia = new URLSearchParams(actual);
          if (texto === '') copia.delete('q');
          else copia.set('q', texto);
          return copia;
        },
        { replace: true },
      );
    }, ESPERA_MS);
    return () => {
      window.clearTimeout(temporizador);
    };
  }, [texto, consultaUrl, setSearchParams]);

  const { data, isFetching, isError, error, refetch } = useQuery<RespuestaDeBusqueda>({
    queryKey: ['busqueda-cartera', consultaUrl, tipoUrl],
    queryFn: ({ signal }) => buscarEnLaCartera(consultaUrl, tipoUrl, signal),
    enabled: autenticado,
    // `staleTime` corto: la cartera cambia mientras se trabaja, y volver a una
    // busqueda de hace un rato tiene que traer lo que hay ahora.
    staleTime: 15 * 1000,
    refetchOnWindowFocus: false,
    // Mantiene los resultados anteriores mientras llega la consulta nueva: sin
    // esto la lista parpadea a vacio en cada tecla.
    placeholderData: (anterior) => anterior,
  });

  function elegirTipo(valor: string): void {
    setSearchParams(
      (actual) => {
        const copia = new URLSearchParams(actual);
        if (valor === TIPO_TODO) copia.delete('tipo');
        else copia.set('tipo', valor);
        return copia;
      },
      { replace: true },
    );
  }

  const respuesta: RespuestaDeBusqueda | undefined = esRespuestaValida(data) ? data : undefined;
  const grupos = respuesta?.grupos ?? [];
  const conFilas = grupos.filter((grupo) => grupo.resultados.length > 0);
  const totalPorTipo = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const grupo of grupos) mapa.set(grupo.tipo, grupo.total);
    return mapa;
  }, [grupos]);

  // El backend falso del archivo suelto contesta 200 con un objeto vacio a toda
  // ruta que no tenga grabada. Si eso pasa, se dice; una pantalla que contesta
  // «sin resultados» para siempre es peor que una que avisa que no esta.
  const sinEndpoint = data !== undefined && !esRespuestaValida(data);

  return (
    <div className="mx-auto w-full max-w-4xl">
      <PageHeader
        title="Buscar en la cartera"
        description="Escribí un nombre, un número o un CUIT. Buscando el nombre de una administración también aparecen sus administradores y sus consorcios."
      />

      <div className="sticky top-topbar z-10 -mx-1 bg-app/90 px-1 pb-3 pt-1 backdrop-blur">
        <div className="flex items-center gap-2.5 rounded-xl border border-line bg-surface px-4 shadow-sm focus-within:border-accent">
          <IconSearch className="h-5 w-5 shrink-0 text-fg-faint" aria-hidden="true" />
          <input
            ref={cajaRef}
            type="search"
            value={texto}
            onChange={(evento) => {
              setTexto(evento.target.value);
            }}
            aria-label="Buscar administraciones, administradores y consorcios"
            autoComplete="off"
            spellCheck={false}
            placeholder="Kloster, ADM-00005, 30-71455236-9…"
            className="h-14 w-full bg-transparent text-base outline-none placeholder:text-fg-faint"
          />
          {isFetching && <IconSpinner className="h-4 w-4 shrink-0 text-fg-faint" />}
        </div>

        {/* Los botones de tipo. Llevan el total de SU tipo aunque se este mirando
            otro: sin eso, quien acota a Consorcios y no encuentra nada no tiene
            forma de saber que lo que buscaba estaba entre las administraciones. */}
        <div role="group" aria-label="Acotar a un tipo" className="mt-2.5 flex flex-wrap gap-1.5">
          {(respuesta?.tipos ?? []).map((opcion) => {
            const activo = tipoUrl === opcion.valor;
            const cuantos =
              opcion.valor === TIPO_TODO ? (respuesta?.total ?? 0) : (totalPorTipo.get(opcion.valor) ?? 0);
            return (
              <button
                key={opcion.valor}
                type="button"
                aria-pressed={activo}
                onClick={() => {
                  elegirTipo(opcion.valor);
                }}
                className={
                  'rounded-full border px-3 py-1 text-sm transition-colors ' +
                  (activo
                    ? 'border-accent bg-accent-soft font-medium text-accent'
                    : 'border-line text-fg-muted hover:text-fg')
                }
              >
                {opcion.etiqueta}
                {respuesta !== undefined && consultaUrl !== '' && (
                  <span className="ml-1.5 tabular-nums text-xs text-fg-faint">{cuantos}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {isError && (
        <ErrorState
          title="No se pudo buscar"
          message={error instanceof ApiError ? error.message : 'Reintentá en un momento.'}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {sinEndpoint && (
        <EmptyState
          title="La búsqueda no está disponible acá"
          description="Esta versión del sistema no trae el buscador general. Se busca desde cada pantalla con el filtro de arriba."
        />
      )}

      {!isError && !sinEndpoint && respuesta !== undefined && (
        <>
          {respuesta.aviso !== '' && (
            <EmptyState title="Buscá en toda la cartera" description={respuesta.aviso} />
          )}

          {respuesta.aviso === '' && conFilas.length === 0 && (
            <EmptyState
              title={`Sin resultados para «${respuesta.q}»`}
              description="Probá con parte del nombre, con el número (ADM-00005, CON-00001) o con el CUIT. Lo archivado no aparece acá."
            />
          )}

          {conFilas.map((grupo) => (
            <GrupoDeResultados
              key={grupo.tipo}
              grupo={grupo}
              acotado={tipoUrl !== TIPO_TODO}
              onVerTodos={() => {
                elegirTipo(grupo.tipo);
              }}
            />
          ))}

          {/* Los grupos que el usuario no puede leer se dicen, no se esconden.
              Ver `GrupoDeBusqueda.visible` en `api.ts`. */}
          {grupos
            .filter((grupo) => !grupo.visible)
            .map((grupo) => (
              <p key={grupo.tipo} className="mt-4 text-sm text-fg-faint">
                No tenés permiso para ver {grupo.etiqueta.toLowerCase()}, así que no se buscó ahí.
              </p>
            ))}
        </>
      )}
    </div>
  );
}

/**
 * El icono del grupo, resuelto contra el MISMO catalogo cerrado que los menus
 * (`ICONOS_DE_MENU`). El nombre lo manda el backend igual que en `ui.py`, asi que
 * lo que no figura en el catalogo cae al generico en vez de romper.
 */
function IconoDelGrupo({ nombre }: { nombre: string }) {
  const Componente = ICONOS_DE_MENU[nombre];
  if (Componente === undefined) return <IconDot className="h-4 w-4 shrink-0 opacity-45" />;
  return <Componente className="h-4 w-4 shrink-0" />;
}

function GrupoDeResultados({
  grupo,
  acotado,
  onVerTodos,
}: {
  grupo: GrupoDeBusqueda;
  acotado: boolean;
  onVerTodos: () => void;
}) {
  const ocultos = grupo.total - grupo.resultados.length;

  return (
    <section className="mt-6" aria-label={grupo.etiqueta}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-fg-faint">
          <IconoDelGrupo nombre={grupo.icono} />
          {grupo.etiqueta}
          <span className="tabular-nums font-normal">{grupo.total}</span>
        </h2>
        {ocultos > 0 && !acotado && (
          <button
            type="button"
            onClick={onVerTodos}
            className="text-sm font-medium text-accent hover:underline"
          >
            Ver los {grupo.total}
          </button>
        )}
      </div>

      <Card className="overflow-hidden">
        <ul className="divide-y divide-line">
          {grupo.resultados.map((fila) => (
            <li key={`${fila.modelo}-${fila.id}`}>
              {/* Un `Link` pelado, sin `onClick` que lo intercepte: asi Ctrl+click
                  y el boton del medio siguen abriendo en una pestaña nueva, que
                  es lo que uno hace cuando quiere comparar dos resultados. */}
              <Link
                to={recordPath(fila.modelo, fila.id)}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-raised"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    {fila.numero !== '' && (
                      <span className="font-mono text-xs text-fg-faint">{fila.numero}</span>
                    )}
                    <span className="truncate font-medium">{fila.titulo}</span>
                    {fila.motivo !== 'propio' && (
                      <Badge tone="neutral">{TEXTO_DEL_MOTIVO[fila.motivo] ?? 'relacionado'}</Badge>
                    )}
                  </div>

                  {/* EL CAMINO. Es la mitad del pedido: de que administracion es
                      cada consorcio. Sin esto, un resultado encontrado por el
                      nombre del padre se lee como ruido. */}
                  {fila.camino.length > 0 && (
                    <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-fg-muted">
                      {fila.camino.map((paso, posicion) => (
                        <span key={`${paso.modelo}-${paso.id}`} className="flex items-center gap-1">
                          {posicion > 0 && <span aria-hidden="true">▸</span>}
                          {paso.etiqueta}
                        </span>
                      ))}
                    </p>
                  )}

                  {fila.detalle !== '' && (
                    <p className="mt-0.5 truncate text-xs text-fg-faint">{fila.detalle}</p>
                  )}
                </div>
                <IconChevronRight className="h-4 w-4 shrink-0 text-fg-faint" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      {ocultos > 0 && acotado && (
        <p className="mt-2 text-xs text-fg-faint">
          Se muestran {grupo.resultados.length} de {grupo.total}. Afiná la búsqueda para ver el
          resto: para recorrer la lista entera está la pantalla de {grupo.etiqueta.toLowerCase()},
          con su paginado y sus filtros.
        </p>
      )}
    </section>
  );
}
